import { useTranslation } from "react-i18next";
import { FilterContent } from "@modules/shop/components/Products/FilterSidebar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@ui/components/ui/sheet";
import { Button } from "@ui/components/ui/button";
import type { useProductFilters } from "@modules/shop/hooks/useProductFilters";
import type { FilterParams } from "@modules/shop/components/Products/filter-params";
import type { GetCollectionByHandleQuery } from "@gql/types";

type ApiFilter = NonNullable<
  GetCollectionByHandleQuery["collection"]
>["products"]["filters"][number];

type MobileFilterDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productFilters: ReturnType<typeof useProductFilters>;
  resultCount: number;
  sortKey: string;
  onSortChange: (value: string) => void;
  availableFilters?: Array<ApiFilter>;
  availableProductTypes?: Array<string>;
};

/** Counts the number of individually active filter dimensions. */
export function getActiveFilterCount(filters: FilterParams): number {
  let count = 0;
  count += filters.type.length;
  count += filters.color.length;
  count += filters.size.length;
  if (filters.minPrice > 0 || filters.maxPrice > 0) count += 1;
  return count;
}

export function MobileFilterDrawer({
  open,
  onOpenChange,
  productFilters,
  resultCount,
  sortKey,
  onSortChange,
  availableFilters,
  availableProductTypes,
}: Readonly<MobileFilterDrawerProps>) {
  const { t } = useTranslation();
  const { isFiltered, resetFilters } = productFilters;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideCloseButton={false}
        className="flex h-full w-full flex-col sm:max-w-md"
      >
        <SheetHeader className="border-border-3 border-b">
          <SheetTitle className="text-text-1">
            {t("Filters.mobile.title")}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t("Filters.mobile.title")}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <FilterContent
            productFilters={productFilters}
            sortKey={sortKey}
            onSortChange={onSortChange}
            availableFilters={availableFilters}
            availableProductTypes={availableProductTypes}
            idPrefix="mobile-"
          />
        </div>

        <SheetFooter className="border-border-3 flex-row border-t">
          {isFiltered && (
            <Button
              dark="ghost"
              size="small"
              onClick={() => {
                resetFilters();
              }}
            >
              {t("Filters.clearAll")}
            </Button>
          )}
          <Button
            primary="default"
            size="small"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            {t("Filters.mobile.showResults", { count: resultCount })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
